import React, { useEffect, useState, useContext } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { Link } from 'react-router-dom';
import { Button, Card, Space, Typography, Alert, Switch } from 'antd';
import { PlusOutlined, TagsOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import ContextSnackbar from 'Components/Snackbar';
import { HeadingTwo } from 'Components/Headings';
import { subRouteCodes as SUB_ROUTES } from 'Constants/routes';
import { DISCOUNT_CODES } from 'Queries/Admin/DiscountCodes';
import DiscountCodesTable from 'Components/DiscountCodes/DiscountCodesTable';
import { TOGGLE_DISCOUNT_CODES_FOR_RENTERS } from 'Mutations/ToggleDiscountCodesForRenters';
import { displayFlex } from '../../../styles/Mixins';
import { paragraphReg } from '../../../styles/Typography';
import colors from '../../../styles/Colors';
import { UserContext } from 'Store/UserContext';

const { Text, Title } = Typography;

const PageContainer = styled.div`
  &&& {
    ${displayFlex}
    align-items: center;
    width: 100%;
    height: auto;
    padding: 12px 0 11px 20px;
    ${paragraphReg}
    font-size: 14px;
    text-align: left;
    background-color: ${colors.background.primary};
    color: ${colors.text};
    border-radius: 3px;
  }
`;

const PageHeader = styled.div`
  text-align: left;
  width: 100%;
  border-bottom: 1px solid rgb(200, 214, 229);
  &&& {
    margin: 65px 0 25px;
  }
`;

const HeaderWrapper = styled.div`
  margin: 20px 0px !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PageBody = styled.div`
  text-align: left;
  width: 100%;
  &&& {
    margin: 0;
    padding: 10px 20px;
  }
`;

const InfoAlert = styled(Alert)`
  margin-bottom: 24px;
`;

const CreateButton = styled(Button)`
  font-family: IBMPlexSans-SemiBold;
  font-weight: 600;
`;

const StatsCard = styled(Card)`
  margin-bottom: 24px;

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 24px;
  }

  .stat-item {
    text-align: center;
  }

  .stat-value {
    font-size: 32px;
    font-weight: 700;
    color: rgb(51, 63, 72);
    display: block;
  }

  .stat-label {
    font-size: 12px;
    color: rgb(100, 116, 139);
    text-transform: uppercase;
    margin-top: 4px;
  }
`;

const RenterSettingsCard = styled(Card)`
  margin-bottom: 24px;

  .setting-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .setting-info {
    flex: 1;
  }

  .setting-title {
    font-family: IBMPlexSans-SemiBold;
    font-size: 14px;
    font-weight: 600;
    color: rgb(51, 63, 72);
    margin-bottom: 4px;
  }

  .setting-description {
    font-size: 13px;
    color: rgb(100, 116, 139);
  }
`;

const DiscountCodes = () => {
  const { user } = useContext(UserContext);
  const venue = user?.venues && user.venues[0];
  const [venueId, setVenueId] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [discountCodesForRenters, setDiscountCodesForRenters] = useState(false);

  const { data: discountCodesData, loading: discountCodesLoading, error: discountCodesError, refetch: refetchDiscountCodes } = useQuery(DISCOUNT_CODES, {
    fetchPolicy: 'network-only'
  });

  const [toggleDiscountCodesForRenters] = useMutation(TOGGLE_DISCOUNT_CODES_FOR_RENTERS, {
    onCompleted: data => {
      if (data.toggleDiscountCodesForRenters.success) {
        const updatedVenue = data.toggleDiscountCodesForRenters.venue;
        setDiscountCodesForRenters(updatedVenue.discountCodesForRenters);
      }
      setToggleLoading(false);
    },
    onError: () => {
      setToggleLoading(false);
    }
  });

  useEffect(() => {
    if (venue?.id) {
      setVenueId(venue.id);
      setDiscountCodesForRenters(venue.discountCodesForRenters || false);
    }
  }, [venue]);

  const handleToggleRenterAccess = async checked => {
    if (!venueId) return;

    setToggleLoading(true);
    await toggleDiscountCodesForRenters({
      variables: {
        input: {
          venueId: venueId,
          discountCodesForRenters: checked
        }
      }
    });
  };

  const discountCodes = discountCodesData?.discountCodes?.discountCodes || [];

  // Calculate statistics
  const stats = {
    total: discountCodes.length,
    active: discountCodes.filter(dc => dc.isActive).length,
    inactive: discountCodes.filter(dc => !dc.isActive).length,
    totalUses: discountCodes.reduce((sum, dc) => sum + (dc.currentUses || 0), 0)
  };

  return (
    <>
      <PageContainer>
        <ContextSnackbar />
        <PageHeader>
          <HeaderWrapper>
            <HeadingTwo label="DISCOUNT CODES" />
            <Link to={SUB_ROUTES.ADMIN.DISCOUNT_CODES_CREATE} style={{ textDecoration: 'none' }}>
              <CreateButton type="primary" icon={<PlusOutlined />} size="large">
                CREATE DISCOUNT CODE
              </CreateButton>
            </Link>
          </HeaderWrapper>
        </PageHeader>
      </PageContainer>

      <PageBody>
        <InfoAlert
          message="Discount Codes Management"
          description="Create and manage discount codes for your venue. Codes can be percentage-based or fixed amounts, with optional usage limits and validity periods. Once created, most fields are immutable to maintain historical accuracy."
          type="info"
          icon={<TagsOutlined />}
          showIcon
        />

        <RenterSettingsCard title="Renter Discount Code Access">
          <div className="setting-row">
            <div className="setting-info">
              <div className="setting-title">Enable Discount Codes for Renters</div>
              <div className="setting-description">When enabled, renters can apply discount codes you share with them during checkout</div>
            </div>
            <Switch checked={discountCodesForRenters} onChange={handleToggleRenterAccess} loading={toggleLoading} />
          </div>
        </RenterSettingsCard>

        {stats.total > 0 && (
          <StatsCard title="Discount Codes Statistics">
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-value">{stats.total}</span>
                <div className="stat-label">Total Codes</div>
              </div>
              <div className="stat-item">
                <span className="stat-value" style={{ color: '#52c41a' }}>
                  {stats.active}
                </span>
                <div className="stat-label">Active Codes</div>
              </div>
              <div className="stat-item">
                <span className="stat-value" style={{ color: '#d9d9d9' }}>
                  {stats.inactive}
                </span>
                <div className="stat-label">Inactive Codes</div>
              </div>
              <div className="stat-item">
                <span className="stat-value" style={{ color: '#1890ff' }}>
                  {stats.totalUses}
                </span>
                <div className="stat-label">Total Uses</div>
              </div>
            </div>
          </StatsCard>
        )}

        <DiscountCodesTable discountCodes={discountCodes} loading={discountCodesLoading} refetch={refetchDiscountCodes} venueId={venueId} isAdmin={true} />

        {discountCodesError && <Alert message="Error loading discount codes" description={discountCodesError.message} type="error" style={{ marginTop: 16 }} />}

        {!discountCodesLoading && discountCodes.length === 0 && (
          <Card style={{ textAlign: 'center', padding: '60px 20px', marginTop: 24 }}>
            <TagsOutlined style={{ fontSize: 64, color: '#d9d9d9', marginBottom: 16 }} />
            <Title level={4} style={{ color: 'rgb(100, 116, 139)' }}>
              No discount codes yet
            </Title>
            <Link to={SUB_ROUTES.ADMIN.DISCOUNT_CODES_CREATE}>
              <Button type="primary" icon={<PlusOutlined />} size="large">
                CREATE YOUR FIRST DISCOUNT CODE
              </Button>
            </Link>
          </Card>
        )}
      </PageBody>
    </>
  );
};

export default DiscountCodes;
